<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import { readCookie, setBrandCookie } from '@/lib/brandCookie'

const { t } = useI18n()

// Theme (light/dark). Public-site-only — stored in the gp_theme cookie for
// the whole getphuket.* domain (so it follows the visitor across the
// subdomains) with localStorage as the same-origin fallback, and applied via
// data-mode on documentElement. The boot script in application.html.erb
// reads the same cookie before paint to prevent flash.
const themeMode = ref('light')

function applyTheme(mode) {
  themeMode.value = mode
  if (typeof document === 'undefined') return
  document.documentElement.dataset.mode = mode
  document.documentElement.style.colorScheme = mode
  // The boot script stamps an inline --bg for flash-free first paint; clear it
  // on a live switch so the stylesheet's themed tokens win again.
  document.documentElement.style.removeProperty('--bg')
  try { setBrandCookie('gp_theme', mode) } catch (e) {}
  try { localStorage.setItem('gp_theme', mode) } catch (e) {}
}

function setTheme(mode) {
  if (mode === themeMode.value) return
  applyTheme(mode)
}

onMounted(() => {
  try {
    const stored = readCookie('gp_theme') || localStorage.getItem('gp_theme')
    if (stored === 'dark' || stored === 'light') {
      themeMode.value = stored
    } else if (document.documentElement.dataset.mode === 'dark') {
      themeMode.value = 'dark'
    }
  } catch (e) {}
})
</script>

<template>
  <HeaderMenu :aria-label="t('public.menu.theme')" wide>
    <template #icon>
      <svg v-if="themeMode === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M20 14.6A8.2 8.2 0 0 1 9.4 4a8.2 8.2 0 1 0 10.6 10.6Z"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3.6"/>
        <path d="M12 3.5v1.8M12 18.7v1.8M3.5 12h1.8M18.7 12h1.8M6 6l1.3 1.3M16.7 16.7L18 18M6 18l1.3-1.3M16.7 7.3L18 6"/>
      </svg>
      <span class="hmenu__label">{{ themeMode === 'dark' ? t('public.menu.dark') : t('public.menu.light') }}</span>
    </template>
    <template #default="{ close }">
      <button type="button" class="hmenu__item" @click="setTheme('light'); close()">
        <span class="hmenu__item-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hmenu__leadicon" aria-hidden="true">
            <circle cx="12" cy="12" r="4"/>
            <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4"/>
          </svg>
          {{ t('public.menu.theme_light') }}
        </span>
        <svg v-if="themeMode === 'light'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </button>
      <button type="button" class="hmenu__item" @click="setTheme('dark'); close()">
        <span class="hmenu__item-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hmenu__leadicon" aria-hidden="true">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/>
          </svg>
          {{ t('public.menu.theme_dark') }}
        </span>
        <svg v-if="themeMode === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </button>
    </template>
  </HeaderMenu>
</template>

<style scoped>
/* These items are authored here (carry this component's scope), so style them
   directly — no :deep needed even though HeaderMenu renders them in its panel. */
.hmenu__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  background: transparent;
  border: 0;
  border-radius: 0.4rem;
  color: var(--ink);
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.hmenu__item:hover { background: color-mix(in oklch, var(--ink) 6%, transparent); }
.hmenu__item-row { display: inline-flex; align-items: center; gap: 0.625rem; }
.hmenu__leadicon { width: 1rem; height: 1rem; color: var(--inkSoft); }
.hmenu__check { width: 1rem; height: 1rem; color: var(--accent); flex-shrink: 0; }
</style>
